<template>
  <div>
    <!-- Timezone -->
    <div class="columns" style="margin-top: 10px">
      <div class="column is-2">
        <span style="font-weight: bold">{{
          $l("components.settings.timezone", "Timezone")
        }}</span>
      </div>

      <div class="column is-6">
        <v-select
          v-model="form.timezone"
          :clearable="false"
          :options="listTimezones"
        >
        </v-select>
      </div>
    </div>

    <!-- Database -->

    <div class="columns" style="margin-top: 10px">
      <div class="column">
        <span style="font-weight: bold">{{
          $l("components.settings.database", "Database")
        }}</span>
      </div>
    </div>

    <div class="columns">
      <div class="column is-2">
        <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
      </div>
      <div class="column is-6">
        <v-select
          v-model="form.database.repeat"
          label="name"
          :reduce="(option) => option.value"
          :clearable="false"
          :options="[
            {
              name: $l('components.settings.beats', 'Beats'),
              value: 'beats',
            },
            {
              name: $l('components.settings.daily', 'Daily'),
              value: 'daily',
            },
            {
              name: $l('components.settings.weekly', 'Weekly'),
              value: 'weekly',
            },
            {
              name: $l('components.settings.none', 'None'),
              value: 'none',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div
      class="columns"
      :class="{ disabled: form.database.repeat !== 'beats' }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.database.interval"
          :clearable="false"
          :options="
            form.database.unit === 'minutes'
              ? listIntervalMinutes
              : listIntervalHours
          "
        >
        </v-select>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.database.unit"
          :clearable="false"
          :reduce="(option) => option.value"
          @input="changeUnit('database')"
          :options="[
            {
              label: $l('components.settings.minutes', 'minutes'),
              value: 'minutes',
            },

            {
              label: $l('components.settings.hours', 'hours'),
              value: 'hours',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div
      class="columns"
      :class="{
        disabled:
          form.database.repeat !== 'daily' && form.database.repeat !== 'weekly',
      }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.startOn", "Start on") }}</span>
      </div>
      <div class="column 6">
        <time-picker :widthProp="13" :time.sync="form.database.time" />
        <span class="clock"
          ><svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.66667 3.75H7.08333V7.5H10.625V9H5.66667V3.75Z"
              fill="#DBDBDB"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.1667 7.5C14.1667 11.6421 10.9954 15 7.08333 15C3.17132 15 0 11.6421 0 7.5C0 3.35786 3.17132 0 7.08333 0C10.9954 0 14.1667 3.35786 14.1667 7.5ZM12.75 7.5C12.75 10.8137 10.2129 13.5 7.08333 13.5C3.95372 13.5 1.41667 10.8137 1.41667 7.5C1.41667 4.18629 3.95372 1.5 7.08333 1.5C10.2129 1.5 12.75 4.18629 12.75 7.5Z"
              fill="#DBDBDB"
            />
          </svg>
        </span>
      </div>
    </div>

    <div
      class="columns"
      :class="{ disabled: form.database.repeat !== 'weekly' }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
      </div>

      <div class="column is-5" style="margin-top: 10px; margin-left: 5px">
        <div class="columns">
          <div
            class="column is-2 day-picker"
            :class="{ selected: day.selected }"
            v-for="(day, i) in listDatabaseDays"
            :key="i"
            @click="selectDay(day, 'database')"
          >
            <span>{{ day.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Engine -->

    <div class="columns" style="margin-top: 10px">
      <div class="column">
        <span style="font-weight: bold">{{
          $l("components.settings.engine", "Engine")
        }}</span>
      </div>
    </div>

    <div class="columns">
      <div class="column is-2">
        <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
      </div>
      <div class="column is-6">
        <v-select
          v-model="form.engine.repeat"
          label="name"
          :reduce="(option) => option.value"
          :clearable="false"
          :options="[
            {
              name: $l('components.settings.beats', 'Beats'),
              value: 'beats',
            },
            {
              name: $l('components.settings.daily', 'Daily'),
              value: 'daily',
            },
            {
              name: $l('components.settings.weekly', 'Weekly'),
              value: 'weekly',
            },
            {
              name: $l('components.settings.none', 'None'),
              value: 'none',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div class="columns" :class="{ disabled: form.engine.repeat !== 'beats' }">
      <div class="column is-2">
        <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.engine.interval"
          :clearable="false"
          :options="
            form.engine.unit === 'minutes'
              ? listIntervalMinutes
              : listIntervalHours
          "
        >
        </v-select>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.engine.unit"
          :clearable="false"
          :reduce="(option) => option.value"
          @input="changeUnit('engine')"
          :options="[
            {
              label: $l('components.settings.minutes', 'minutes'),
              value: 'minutes',
            },

            {
              label: $l('components.settings.hours', 'hours'),
              value: 'hours',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div
      class="columns"
      :class="{
        disabled:
          form.engine.repeat !== 'daily' && form.engine.repeat !== 'weekly',
      }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.startOn", "Start on") }}</span>
      </div>
      <div class="column 6">
        <time-picker :widthProp="13" :time.sync="form.engine.time" />
        <span class="clock"
          ><svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.66667 3.75H7.08333V7.5H10.625V9H5.66667V3.75Z"
              fill="#DBDBDB"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.1667 7.5C14.1667 11.6421 10.9954 15 7.08333 15C3.17132 15 0 11.6421 0 7.5C0 3.35786 3.17132 0 7.08333 0C10.9954 0 14.1667 3.35786 14.1667 7.5ZM12.75 7.5C12.75 10.8137 10.2129 13.5 7.08333 13.5C3.95372 13.5 1.41667 10.8137 1.41667 7.5C1.41667 4.18629 3.95372 1.5 7.08333 1.5C10.2129 1.5 12.75 4.18629 12.75 7.5Z"
              fill="#DBDBDB"
            />
          </svg>
        </span>
      </div>
    </div>

    <div class="columns" :class="{ disabled: form.engine.repeat !== 'weekly' }">
      <div class="column is-2">
        <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
      </div>

      <div class="column is-5" style="margin-top: 10px; margin-left: 5px">
        <div class="columns">
          <div
            class="column is-2 day-picker"
            :class="{ selected: day.selected }"
            v-for="(day, i) in listEngineDays"
            :key="i"
            @click="selectDay(day, 'engine')"
          >
            <span>{{ day.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Installer -->

    <div class="columns" style="margin-top: 10px">
      <div class="column">
        <span style="font-weight: bold">{{
          $l("components.settings.installer", "Installer")
        }}</span>
      </div>
    </div>

    <div class="columns">
      <div class="column is-2">
        <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
      </div>
      <div class="column is-6">
        <v-select
          v-model="form.installer.repeat"
          label="name"
          :reduce="(option) => option.value"
          :clearable="false"
          :options="[
            {
              name: $l('components.settings.beats', 'Beats'),
              value: 'beats',
            },
            {
              name: $l('components.settings.daily', 'Daily'),
              value: 'daily',
            },
            {
              name: $l('components.settings.weekly', 'Weekly'),
              value: 'weekly',
            },
            {
              name: $l('components.settings.none', 'None'),
              value: 'none',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div
      class="columns"
      :class="{ disabled: form.installer.repeat !== 'beats' }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.installer.interval"
          :clearable="false"
          :options="
            form.engine.unit === 'minutes'
              ? listIntervalMinutes
              : listIntervalHours
          "
        >
        </v-select>
      </div>
      <div class="column is-3">
        <v-select
          v-model="form.installer.unit"
          :clearable="false"
          :reduce="(option) => option.value"
          @input="changeUnit('installer')"
          :options="[
            {
              label: $l('components.settings.minutes', 'minutes'),
              value: 'minutes',
            },

            {
              label: $l('components.settings.hours', 'hours'),
              value: 'hours',
            },
          ]"
        >
        </v-select>
      </div>
    </div>

    <div
      class="columns"
      :class="{
        disabled:
          form.installer.repeat !== 'daily' &&
          form.installer.repeat !== 'weekly',
      }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.startOn", "Start on") }}</span>
      </div>
      <div class="column is-6">
        <time-picker :widthProp="13" :time.sync="form.installer.time" />
        <span class="clock"
          ><svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.66667 3.75H7.08333V7.5H10.625V9H5.66667V3.75Z"
              fill="#DBDBDB"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.1667 7.5C14.1667 11.6421 10.9954 15 7.08333 15C3.17132 15 0 11.6421 0 7.5C0 3.35786 3.17132 0 7.08333 0C10.9954 0 14.1667 3.35786 14.1667 7.5ZM12.75 7.5C12.75 10.8137 10.2129 13.5 7.08333 13.5C3.95372 13.5 1.41667 10.8137 1.41667 7.5C1.41667 4.18629 3.95372 1.5 7.08333 1.5C10.2129 1.5 12.75 4.18629 12.75 7.5Z"
              fill="#DBDBDB"
            />
          </svg>
        </span>
      </div>
    </div>

    <div
      class="columns"
      :class="{ disabled: form.installer.repeat !== 'weekly' }"
    >
      <div class="column is-2">
        <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
      </div>

      <div class="column is-5" style="margin-top: 10px; margin-left: 5px">
        <div class="columns">
          <div
            class="column is-2 day-picker"
            :class="{ selected: day.selected }"
            v-for="(day, i) in listInstallerDays"
            :key="i"
            @click="selectDay(day, 'installer')"
          >
            <span>{{ day.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="columns" style="margin-top: 30px">
      <div class="column is-8">
        <div slot="footer" class="field is-grouped is-grouped-right">
          <div class="control">
            <button
              class="button is-info"
              @click.prevent="updateSchedule"
              v-if="isAdmin"
            >
              {{ $l("global.update", "Update") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SettingsApi from "@/api/Settings";
import vSelect from "vue-select";
import moment from "moment-timezone";
import TimePicker from "@/components/TimePicker.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    vSelect,
    TimePicker,
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  data() {
    return {
      listIntervalMinutes: [],
      listIntervalHours: [],
      listTimezones: [],
      listDatabaseDays: [
        {
          label: "SUN",
          selected: false,
          value: "0",
        },
        {
          label: "MON",
          selected: false,
          value: "1",
        },
        {
          label: "TUE",
          selected: false,
          value: "2",
        },
        {
          label: "WED",
          selected: false,
          value: "3",
        },
        {
          label: "THU",
          selected: false,
          value: "4",
        },
        {
          label: "FRI",
          selected: false,
          value: "5",
        },
        {
          label: "SAT",
          selected: false,
          value: "6",
        },
      ],

      listEngineDays: [
        {
          label: "SUN",
          selected: false,
          value: "0",
        },
        {
          label: "MON",
          selected: false,
          value: "1",
        },
        {
          label: "TUE",
          selected: false,
          value: "2",
        },
        {
          label: "WED",
          selected: false,
          value: "3",
        },
        {
          label: "THU",
          selected: false,
          value: "4",
        },
        {
          label: "FRI",
          selected: false,
          value: "5",
        },
        {
          label: "SAT",
          selected: false,
          value: "6",
        },
      ],

      listInstallerDays: [
        {
          label: "SUN",
          selected: false,
          value: "0",
        },
        {
          label: "MON",
          selected: false,
          value: "1",
        },
        {
          label: "TUE",
          selected: false,
          value: "2",
        },
        {
          label: "WED",
          selected: false,
          value: "3",
        },
        {
          label: "THU",
          selected: false,
          value: "4",
        },
        {
          label: "FRI",
          selected: false,
          value: "5",
        },
        {
          label: "SAT",
          selected: false,
          value: "6",
        },
      ],

      form: {
        timezone: "Asia/Ho_Chi_Minh",
        database: {
          repeat: "beats",
          time: "",
          interval: 0,
          unit: "",
          date: null,
        },
        engine: {
          repeat: "beats",
          time: "",
          interval: 0,
          unit: "",
          date: null,
        },
        installer: {
          repeat: "beats",
          time: "",
          interval: 0,
          unit: "",
          date: null,
        },
      },
    };
  },

  methods: {
    changeUnit(type) {
      if (type === "database") {
        this.form.database.interval = null;
      }

      if (type === "engine") {
        this.form.engine.interval = null;
      }

      if (type === "installer") {
        this.form.installer.interval = null;
      }
    },

    selectDay(day, type) {
      if (!day.selected) {
        day.selected = true;

        if (type === "database") {
          this.form.database.date.push(day.value);
        }

        if (type === "engine") {
          this.form.engine.date.push(day.value);
        }

        if (type === "installer") {
          this.form.installer.date.push(day.value);
        }
      } else {
        day.selected = false;

        if (type === "database") {
          this.form.database.date = this.form.database.date.filter(function (
            element
          ) {
            return element !== day.value;
          });
        }

        if (type === "engine") {
          this.form.engine.date = this.form.engine.date.filter(function (
            element
          ) {
            return element !== day.value;
          });
        }

        if (type === "installer") {
          this.form.installer.date = this.form.installer.date.filter(function (
            element
          ) {
            return element !== day.value;
          });
        }
      }
    },

    setSendingForm() {
      let sendingForm = {
        timezone: "",
        database: {},
        engine: {},
        installer: {},
      };

      if (this.form.timezone) {
        sendingForm.timezone = this.form.timezone;
      }

      if (this.form.database.repeat === "none") {
        sendingForm.database.repeat = "none";
      }

      if (this.form.database.repeat === "beats") {
        sendingForm.database.repeat = "beats";
        sendingForm.database.unit = this.form.database.unit;
        sendingForm.database.interval = this.form.database.interval;
      }

      if (this.form.database.repeat === "daily") {
        sendingForm.database.repeat = "daily";
        sendingForm.database.time = this.form.database.time;
      }

      if (this.form.database.repeat === "weekly") {
        sendingForm.database.repeat = "weekly";
        sendingForm.database.time = this.form.database.time;
        sendingForm.database.date = this.form.database.date;
      }

      if (this.form.engine.repeat === "none") {
        sendingForm.engine.repeat = "none";
      }

      if (this.form.engine.repeat === "beats") {
        sendingForm.engine.repeat = "beats";
        sendingForm.engine.unit = this.form.engine.unit;
        sendingForm.engine.interval = this.form.engine.interval;
      }

      if (this.form.engine.repeat === "daily") {
        sendingForm.engine.repeat = "daily";
        sendingForm.engine.time = this.form.engine.time;
      }

      if (this.form.engine.repeat === "weekly") {
        sendingForm.engine.repeat = "weekly";

        sendingForm.engine.time = this.form.engine.time;
        sendingForm.engine.date = this.form.engine.date;
      }

      if (this.form.installer.repeat === "none") {
        sendingForm.installer.repeat = "none";
      }

      if (this.form.installer.repeat === "beats") {
        sendingForm.installer.repeat = "beats";
        sendingForm.installer.unit = this.form.installer.unit;
        sendingForm.installer.interval = this.form.installer.interval;
      }

      if (this.form.installer.repeat === "daily") {
        sendingForm.installer.repeat = "daily";
        sendingForm.installer.time = this.form.installer.time;
      }

      if (this.form.installer.repeat === "weekly") {
        sendingForm.installer.repeat = "weekly";
        sendingForm.installer.time = this.form.installer.time;
        sendingForm.installer.date = this.form.installer.date;
      }

      return sendingForm;
    },

    updateSchedule() {
      let sendingForm = this.setSendingForm();
      SettingsApi.scheduleUpdate(this.$http, sendingForm).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });

        this.listDatabaseDays = [
          {
            label: "SUN",
            selected: false,
            value: "0",
          },
          {
            label: "MON",
            selected: false,
            value: "1",
          },
          {
            label: "TUE",
            selected: false,
            value: "2",
          },
          {
            label: "WED",
            selected: false,
            value: "3",
          },
          {
            label: "THU",
            selected: false,
            value: "4",
          },
          {
            label: "FRI",
            selected: false,
            value: "5",
          },
          {
            label: "SAT",
            selected: false,
            value: "6",
          },
        ];

        this.listInstallerDays = [
          {
            label: "SUN",
            selected: false,
            value: "0",
          },
          {
            label: "MON",
            selected: false,
            value: "1",
          },
          {
            label: "TUE",
            selected: false,
            value: "2",
          },
          {
            label: "WED",
            selected: false,
            value: "3",
          },
          {
            label: "THU",
            selected: false,
            value: "4",
          },
          {
            label: "FRI",
            selected: false,
            value: "5",
          },
          {
            label: "SAT",
            selected: false,
            value: "6",
          },
        ];
        this.listEngineDays = [
          {
            label: "SUN",
            selected: false,
            value: "0",
          },
          {
            label: "MON",
            selected: false,
            value: "1",
          },
          {
            label: "TUE",
            selected: false,
            value: "2",
          },
          {
            label: "WED",
            selected: false,
            value: "3",
          },
          {
            label: "THU",
            selected: false,
            value: "4",
          },
          {
            label: "FRI",
            selected: false,
            value: "5",
          },
          {
            label: "SAT",
            selected: false,
            value: "6",
          },
        ];

        this.getScheduleUpdate();
      });
    },

    getScheduleUpdate() {
      const today = (new Date().getDay() - 1).toString();
      SettingsApi.getScheduleUpdate(this.$http).then((res) => {
        this.form = {
          timezone: res.timezone || "Asia/Ho_Chi_Minh",
          database: {
            repeat: res.database.repeat ? res.database.repeat : "beats",
            time: res.database.time ? res.database.time : "00:00",
            interval: res.database.interval,
            unit: res.database.unit ? res.database.unit : "hours",
            date: res.database.date ? res.database.date : [today],
          },

          engine: {
            repeat: res.engine.repeat ? res.engine.repeat : "beats",
            time: res.engine.time ? res.engine.time : "00:00",
            interval: res.engine.interval,
            unit: res.engine.unit ? res.engine.unit : "hours",
            date: res.engine.date ? res.engine.date : [today],
          },

          installer: {
            repeat: res.installer.repeat ? res.installer.repeat : "beats",
            time: res.installer.time ? res.installer.time : "00:00",
            interval: res.installer.interval,
            unit: res.installer.unit ? res.engine.unit : "hours",
            date: res.installer.date ? res.installer.date : [today],
          },
        };

        if (this.form.database.date) {
          this.form.database.date.map((day) => {
            const x = this.listDatabaseDays.find((e) => e.value === day);
            if (x) {
              x.selected = true;
            }
          });
        }

        if (this.form.engine.date) {
          this.form.engine.date.map((day) => {
            const x = this.listEngineDays.find((e) => e.value === day);
            if (x) {
              x.selected = true;
            }
          });
        }

        if (this.form.installer.date) {
          this.form.installer.date.map((day) => {
            const x = this.listInstallerDays.find((e) => e.value === day);
            if (x) {
              x.selected = true;
            }
          });
        }
      });
    },
  },

  created() {
    this.listTimezones = moment.tz.names();
    for (let i = 5; i <= 180; i++) {
      this.listIntervalMinutes.push(i);
    }

    for (let i = 1; i <= 24; i++) {
      this.listIntervalHours.push(i);
    }

    this.getScheduleUpdate();
  },
};
</script>

<style src="vue-select/dist/vue-select.css"></style>

<style lang="scss" scoped>
.clock {
  position: relative;
  right: 20px;
  top: 9px;
}

.day-picker {
  height: 30px;
  border-radius: 3px;
  border: 1px solid #c7cdcf;
  text-align: center;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.selected {
    border: 2px solid #3081ed;
    color: #3081ed;
  }
}
</style>
